import { ApplicationInsights } from "@microsoft/applicationinsights-web";

import { getUserFromStorage, RelodeApiError } from "./";
import { RelodeErrorBase } from "./types";

declare const window: any;

let isApplicationInsightsInitialized = false;
let isApplicationStarted = false;
let appInsights: ApplicationInsights = null;

const getUrl = () => {
    if (window && window.location) {
        return `${window.location.pathname}${window.location.search}`;
    }
    return "url unknown";
};

const init = () => {
    const { applicationInsightsKey } = appConfig;
    if (!applicationInsightsKey) return false;
    if (!isApplicationInsightsInitialized) {
        try {
            appInsights = new ApplicationInsights({
                config: {
                    instrumentationKey: applicationInsightsKey,

                    // we explicitly log in our error boundary
                    disableExceptionTracking: true,

                    disableAjaxTracking: true,
                    disableFetchTracking: true,
                },
            });
            appInsights.loadAppInsights();
            appInsights.trackPageView();
            isApplicationInsightsInitialized = true;
        } catch (Err) {
            isApplicationInsightsInitialized = false;
        }
    }
    return isApplicationInsightsInitialized;
};

const applicationStarted = (appName: string) => {
    if (!isApplicationStarted) {
        trackEvent(`${appName} application started`);
        isApplicationStarted = true;
    }
};

type UserLoginInfo = {
    email: string;
    applicationName?: string;
    fullName?: string;
    roleId?: number;
};

const userLogin = (user: UserLoginInfo) => {
    setAuthenticatedUserContext(user.email);

    //Help Scout User Identify and Form Prefill
    if (!!window.Beacon) {
        window.Beacon("identify", {
            name: user.fullName,
            email: user.email,
        });
        window.Beacon("prefill", {
            name: user.fullName,
            email: user.email,
        });
    }

    //PostHog User Identify and Set
    if (!!window.posthog) {
        window.posthog.identify(user.email);

        window.posthog.people.set({
            email: user.email,
            fullName: user.fullName,
            roleId: user.roleId,
        });
    }
};

const userLogout = () => {
    clearAuthenticatedUserContext();

    if (!!window.Beacon) {
        window.Beacon("destroy");
    }

    if (!!window.posthog) {
        window.posthog.reset();
    }
};

const trackRoute = (name, url, properties?) => {
    const props = {
        ...properties,
        user_email: getUserEmail(),
        buildNumber: appConfig.buildNumber,
    };
    if (telemetryService.init()) {
        console.log(`telemetry | trackRoute: ${name}`, url, props);

        appInsights.trackPageView({
            name,
            uri: url,
            properties: props,
        });
    }
};

const trackEvent = (name, properties?) => {
    const props = flattenProps({
        ...properties,
        user_email: getUserEmail(),
        buildNumber: appConfig.buildNumber,
    });
    if (telemetryService.init()) {
        console.log(`telemetry | trackEvent: ${name}`, props);
        appInsights.trackEvent({ name, properties: props });
    }
};
const ignoredExceptions = [400, 409, 415];

const trackException = (
    exception: RelodeErrorBase,
    properties?: { [key: string]: string },
    force: boolean = false
) => {
    if (exception instanceof RelodeApiError || exception instanceof Response) {
        if (!force && ignoredExceptions.some(e => e === exception.status))
            return;
        // we are not logging the above status codes
    }
    const telemetryData =
        exception.getTelemetryData !== undefined
            ? exception.getTelemetryData()
            : {
                  data:
                      "error object did not have 'getTelemetryData' function.",
              };
    const props = {
        user_email: getUserEmail(),
        buildNumber: appConfig.buildNumber,
        current_window_location: getUrl(),
        ...telemetryData,
        ...properties,
    };

    //we want to see these in lucky orange
    console.error("telemetry.service.trackException", exception, props);

    if (telemetryService.init()) {
        appInsights.trackException({
            exception: exception,
            properties: props,
        });
    }
};

const setAuthenticatedUserContext = (authenticatedUserId: string) => {
    if (telemetryService.init()) {
        appInsights.setAuthenticatedUserContext(authenticatedUserId);
    }
};

const clearAuthenticatedUserContext = () => {
    if (telemetryService.init()) {
        appInsights.clearAuthenticatedUserContext();
    }
};

const getUserEmail = () => {
    const user = getUserFromStorage();

    if (!!user) return user.email;

    return "unknown user email";
};

export const telemetryService = {
    applicationStarted,
    init,
    trackRoute,
    trackEvent,
    trackException,
    userLogin,
    userLogout,
};

const flattenProps = (props: any) => {
    if (typeof props !== "object") return props;
    const result = {};
    const keys = Object.keys(props);
    keys.forEach(k => {
        let value = props[k];
        if (typeof value === "object") {
            value = JSON.stringify(value);
        }
        result[k] = value;
    });

    return result;
};
